import { createReducer } from '@reduxjs/toolkit';
import * as AM_COMMON from '@innovyze/lib-am-common/Reducers';
import {
	upsertFailureDefinition,
	upsertFailureDefinitionRejected,
	upsertFailureDefinitionResolved,
	runFailureDefinition,
	runFailureDefinitionResolved,
	runFailureDefinitionRejected,
	getFailureDefinitionAction,
	getFailureDefinitionRejected,
	getFailureDefinitionResolved,
	listAllFailureDefinitions,
	listAllFailureDefinitionsRejected,
	listAllFailureDefinitionsResolved,
	getFailureDefinitionRunResults,
	getFailureDefinitionRunResultsResolved,
	getFailureDefinitionRunResultsRejected,
	setFailureDefinitionPaginationState,
	clearFailureDefinitionPaginationState,
	updateFailureDefinitionPublishedStatusResolved,
	renameFailureDefinition,
	renameFailureDefinitionResolved,
	renameFailureDefinitionRejected,
} from '@Actions/failureDefinition.actions';
import { FailureDefinitionStoreState } from '@Types';

const initialState: FailureDefinitionStoreState = {
	inProcess: false,
	successful: null,
	id: null,
	run: {
		inProcess: false,
		successful: null,
	},
	allFailureDefinitionsList: {
		inProcess: false,
		successful: null,
		failureDefinitions: [],
	},
	failureDefinition: {
		inProcess: false,
		successful: null,
		failureDefinition: null,
	},
	failureDefinitionRunResults: AM_COMMON.paginationInitialState,
	paginationState: {},
};

export const failureDefinitionReducer = createReducer(initialState, builder => {
	builder
		.addCase(upsertFailureDefinition, state => {
			state.inProcess = true;
			state.successful = null;
			state.id = null;
			state.run.inProcess = false;
			state.run.successful = null;
		})
		.addCase(upsertFailureDefinitionResolved, (state, action) => {
			state.inProcess = false;
			state.successful = true;
			state.id = action.payload.id;
		})
		.addCase(upsertFailureDefinitionRejected, state => {
			state.inProcess = false;
			state.successful = false;
			state.id = null;
			state.run.inProcess = false;
			state.run.successful = null;
		})
		.addCase(runFailureDefinition, state => {
			state.run.inProcess = true;
			state.run.successful = null;
		})
		.addCase(runFailureDefinitionResolved, state => {
			state.run.inProcess = false;
			state.run.successful = true;
		})
		.addCase(runFailureDefinitionRejected, state => {
			state.run.inProcess = false;
			state.run.successful = false;
		})
		.addCase(listAllFailureDefinitions, state => {
			state.allFailureDefinitionsList = {
				inProcess: true,
				successful: null,
				failureDefinitions: [],
			};
		})
		.addCase(listAllFailureDefinitionsRejected, state => {
			state.allFailureDefinitionsList = {
				inProcess: false,
				successful: false,
				failureDefinitions: [],
			};
		})
		.addCase(listAllFailureDefinitionsResolved, (state, action) => {
			state.allFailureDefinitionsList = {
				inProcess: false,
				successful: true,
				failureDefinitions: action.payload,
			};
		})
		.addCase(getFailureDefinitionAction, state => {
			state.failureDefinition = {
				inProcess: true,
				successful: null,
				failureDefinition: null,
			};
		})
		.addCase(getFailureDefinitionResolved, (state, action) => {
			state.failureDefinition = {
				inProcess: false,
				successful: true,
				failureDefinition: action.payload,
			};
		})
		.addCase(getFailureDefinitionRejected, state => {
			state.failureDefinition = {
				inProcess: false,
				successful: false,
				failureDefinition: null,
			};
		})
		.addCase(getFailureDefinitionRunResults, state => {
			state.failureDefinitionRunResults = AM_COMMON.updateIsLoading(state.failureDefinitionRunResults, true);
		})
		.addCase(getFailureDefinitionRunResultsResolved, (state, action) => {
			state.failureDefinitionRunResults = AM_COMMON.updateResolved(state.failureDefinitionRunResults, action.payload);
		})
		.addCase(getFailureDefinitionRunResultsRejected, (state, action) => {
			state.failureDefinitionRunResults = AM_COMMON.updateRejected(state.failureDefinitionRunResults, action.payload);
		})
		.addCase(setFailureDefinitionPaginationState, (state, action) => {
			const { failureDefinitionId, paginationRequest } = action.payload;
			state.paginationState[failureDefinitionId] = paginationRequest;
		})
		.addCase(clearFailureDefinitionPaginationState, (state, action) => {
			const { failureDefinitionId } = action.payload;
			delete state.paginationState[failureDefinitionId];
		})
		.addCase(updateFailureDefinitionPublishedStatusResolved, (state, action) => {
			const failureDefIndex = state.allFailureDefinitionsList.failureDefinitions.findIndex(
				fd => fd.id === action.payload.id,
			);
			if (failureDefIndex !== -1) {
				state.allFailureDefinitionsList.failureDefinitions[failureDefIndex].published = action.payload.published;
			}

			if (
				state.failureDefinition.failureDefinition &&
				state.failureDefinition.failureDefinition.id === action.payload.id
			) {
				state.failureDefinition.failureDefinition.published = action.payload.published;
			}
		})
		.addCase(renameFailureDefinition, state => {
			state.inProcess = true;
			state.successful = null;
		})
		.addCase(renameFailureDefinitionResolved, (state, action) => {
			state.inProcess = false;
			state.successful = true;
			if (state.failureDefinition.failureDefinition?.id === action.payload.id) {
				state.failureDefinition.failureDefinition.name = action.payload.name;
			}
			const fdInList = state.allFailureDefinitionsList.failureDefinitions.find(fd => fd.id === action.payload.id);
			if (fdInList) {
				fdInList.name = action.payload.name;
			}
		})
		.addCase(renameFailureDefinitionRejected, state => {
			state.inProcess = false;
			state.successful = false;
		});
});

export default failureDefinitionReducer;
